import range from "lodash/range";
import { Map, List } from "immutable";

import { ACTIONS } from "features/planner/planner.constants";
import { APP_ACTIONS, EMPTY_MAP, EMPTY_LIST } from "app/app.constants";
import { ViewGroupMapper, ViewMapper } from "features/planner/planner.mappers";

const initialState = Map({
  byId: EMPTY_MAP,
});

export default (state = initialState, action) => {
  const { type, error, payload } = action;

  switch (type) {
    case ACTIONS.CREATE_VIEW_GROUPS:
    case ACTIONS.UPDATE_VIEW_GROUPS_ORDER:
    case ACTIONS.GET_VIEW: {
      if (error) return state;

      const view = ViewMapper.from(payload);

      const viewGroups = List(
        payload.viewGroups.map(ViewGroupMapper.from),
      ).sortBy(viewGroup => viewGroup.get("viewColumnOrder"));

      return state
        .setIn(
          ["byTypeAndColumn"],
          viewGroups
            .groupBy(viewGroup => viewGroup.get("type").toString())
            .map((viewGroupsByType, groupType) => {
              const columnCount = view.getIn(["columnCount", groupType]);

              const viewGroupsByViewColumn = viewGroupsByType
                .groupBy(viewGroup => viewGroup.get("viewColumn"))
                .map(viewGroupsByColumn =>
                  viewGroupsByColumn.map(viewGroup => viewGroup.get("id")),
                );

              return List(range(0, columnCount)).map(viewColumn =>
                viewGroupsByViewColumn.get(viewColumn, EMPTY_LIST),
              );
            }),
        )
        .set(
          "byId",
          Map(
            viewGroups.map(viewGroup => [
              viewGroup.get("id").toString(),
              viewGroup,
            ]),
          ),
        );
    }

    case ACTIONS.DELETE_VIEW_COLUMN: {
      const { viewGroupType, viewColumn } = action;

      return state.updateIn(
        ["byTypeAndColumn", viewGroupType.toString()],
        viewColumns =>
          viewColumns
            ? viewColumns
                .delete(viewColumn)
                .update(Math.max(viewColumn - 1, 0), viewGroupIds =>
                  viewGroupIds
                    ? viewGroupIds.concat(viewColumns.get(viewColumn))
                    : undefined,
                )
            : undefined,
      );
    }

    case ACTIONS.ADD_VIEW_COLUMN: {
      const { viewGroupType } = action;

      return state.updateIn(
        ["byTypeAndColumn", viewGroupType.toString()],
        viewColumns =>
          viewColumns
            ? viewColumns.push(EMPTY_LIST)
            : EMPTY_LIST.push(EMPTY_LIST),
      );
    }

    case ACTIONS.SORT_VIEW_GROUP: {
      const {
        groupType,
        targetIndex,
        sourceIndex,
        targetViewColumn,
        sourceViewGroupId,
        sourceViewColumn,
      } = action;

      return state
        .deleteIn([
          "byTypeAndColumn",
          groupType.toString(),
          sourceViewColumn,
          sourceIndex,
        ])
        .updateIn(
          ["byTypeAndColumn", groupType.toString(), targetViewColumn],
          viewGroups =>
            viewGroups
              ? viewGroups.insert(targetIndex, sourceViewGroupId)
              : List.of(sourceViewGroupId),
        );
    }

    case ACTIONS.GET_VIEW_GROUP: {
      if (error) return state;

      const viewGroup = ViewGroupMapper.from(payload);

      return state.setIn(["byId", viewGroup.get("id").toString()], viewGroup);
    }

    case ACTIONS.DELETE_VIEW_GROUP: {
      if (error) return state;

      const { viewGroupId, viewGroup } = action;

      return state
        .deleteIn(["byId", viewGroupId.toString()])
        .updateIn(
          [
            "byTypeAndColumn",
            viewGroup.get("type").toString(),
            viewGroup.get("viewColumn"),
          ],
          viewGroupIds => viewGroupIds.filterNot(vgId => vgId === viewGroupId),
        );
    }

    case ACTIONS.CLEAR_VIEW: {
      return initialState;
    }

    case APP_ACTIONS.ASYNC_START: {
      switch (action.subtype) {
        case ACTIONS.UPDATE_VIEW_GROUP: {
          const { viewGroup, viewGroupId } = action;

          return state.setIn(["byId", viewGroupId.toString()], viewGroup);
        }

        default:
          return state;
      }
    }

    default:
      return state;
  }
};
